/**
 * Mock data backing the Analytics page.
 *
 * A single "universe" of ~250 vessels drives all the Marine-Hull sample
 * visualisations + filters. Separate time-series and counter datasets
 * back the processing/ROI alternatives.
 *
 * Deterministic — same seed yields same output — so filter slicing is stable.
 */

import type {
  AnalyticsSummary,
  AutomationRatePoint,
  ClassSociety,
  CoverholderOption,
  DeductibleRow,
  FieldCompletenessDatum,
  FileBeforeAfter,
  HumanAttentionSegment,
  IsoDate,
  KbGrowthPoint,
  NavZone,
  NavZoneExposureRow,
  Vessel,
  VesselFlag,
  WorkDoneCounter,
} from "@/types/analytics";

// ── Deterministic PRNG ──────────────────────────────────────

/** mulberry32 — small, fast, stable across runs. */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rng: () => number, items: readonly T[]): T {
  const idx = Math.floor(rng() * items.length);
  return items[idx] ?? (items[0] as T);
}

function weighted<T>(rng: () => number, choices: readonly (readonly [T, number])[]): T {
  const total = choices.reduce((a, [, w]) => a + w, 0);
  let r = rng() * total;
  for (const [v, w] of choices) {
    if ((r -= w) <= 0) return v;
  }
  return choices[0]![0];
}

function logNormal(rng: () => number, mu: number, sigma: number): number {
  // Box–Muller.
  const u1 = Math.max(rng(), 1e-9);
  const u2 = rng();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return Math.exp(mu + sigma * z);
}

// ── Reference lists ─────────────────────────────────────────

export const COVERHOLDERS: CoverholderOption[] = [
  { id: "all", label: "All coverholders" },
  { id: "hartwell", label: "Hartwell Marine" },
  { id: "nordic", label: "Nordic Underwriting" },
  { id: "atlas", label: "Atlas Marine Ltd" },
  { id: "coastal", label: "Coastal Re" },
];

const COVERHOLDER_IDS = ["hartwell", "nordic", "atlas", "coastal"] as const;

const FLAGS: Array<{ iso2: string; name: string; isoNum: string; weight: number }> = [
  { iso2: "PA", name: "Panama", isoNum: "591", weight: 23 },
  { iso2: "MH", name: "Marshall Islands", isoNum: "584", weight: 17 },
  { iso2: "LR", name: "Liberia", isoNum: "430", weight: 15 },
  { iso2: "BS", name: "Bahamas", isoNum: "044", weight: 10 },
  { iso2: "GR", name: "Greece", isoNum: "300", weight: 8 },
  { iso2: "GB", name: "United Kingdom", isoNum: "826", weight: 7 },
  { iso2: "IE", name: "Ireland", isoNum: "372", weight: 5 },
  { iso2: "NL", name: "Netherlands", isoNum: "528", weight: 4 },
  { iso2: "NO", name: "Norway", isoNum: "578", weight: 3 },
  { iso2: "TR", name: "Turkey", isoNum: "792", weight: 2 },
];

const TYPES: Array<{
  typeKey: "tanker" | "bulk" | "container" | "gas" | "general" | "other";
  labels: string[];
  weight: number;
}> = [
  {
    typeKey: "tanker",
    labels: ["Tanker — Crude", "Tanker — Product", "Tanker — Chemical"],
    weight: 35,
  },
  { typeKey: "bulk", labels: ["Bulk Carrier"], weight: 22 },
  { typeKey: "container", labels: ["Container Ship"], weight: 18 },
  { typeKey: "gas", labels: ["Gas Carrier LNG", "Gas Carrier LPG"], weight: 12 },
  { typeKey: "general", labels: ["General Cargo"], weight: 8 },
  { typeKey: "other", labels: ["Ro-Ro", "Reefer", "Heavy Lift"], weight: 5 },
];

const CLASS_SOCIETIES: Array<[ClassSociety, number]> = [
  ["LR", 31],
  ["DNV", 24],
  ["BV", 18],
  ["ABS", 14],
  ["RINA", 8],
  ["NKK", 5],
];

const NAV_ZONES: Array<[NavZone, number]> = [
  ["worldwide", 94],
  ["red_sea", 2],
  ["hormuz", 1.5],
  ["black_sea", 1.5],
  ["gulf_of_guinea", 1],
];

// ── Vessel universe ─────────────────────────────────────────

function buildVesselUniverse(count: number): Vessel[] {
  const rng = makeRng(0xbada55);
  const vessels: Vessel[] = [];

  // Date spread: 18 months ending policy expiry
  const mockToday = new Date("2026-03-13T00:00:00Z").getTime();
  const spreadMs = 18 * 30 * 24 * 60 * 60 * 1000;

  for (let i = 0; i < count; i++) {
    const type = weighted(
      rng,
      TYPES.map((t) => [t, t.weight] as const)
    );
    const flag = weighted(
      rng,
      FLAGS.map((f) => [f, f.weight] as const)
    );
    const classSociety = weighted(rng, CLASS_SOCIETIES);
    const navZone = weighted(rng, NAV_ZONES);
    const coverholder = pick(rng, COVERHOLDER_IDS);

    const yearBuilt = weighted(rng, [
      [1985 + Math.floor(rng() * 5), 3],
      [1990 + Math.floor(rng() * 10), 6],
      [2000 + Math.floor(rng() * 10), 30],
      [2010 + Math.floor(rng() * 10), 45],
      [2020 + Math.floor(rng() * 4), 16],
    ] as Array<[number, number]>);

    const coverBasis = rng() < 0.82 ? "hm_full" : "tlo";

    // SI USD — log-normal around $15M, tail out to $120M. Tankers/gas skew higher.
    const mu = type.typeKey === "gas" ? 17.2 : type.typeKey === "tanker" ? 16.6 : 15.8;
    const siUsd = Math.round(logNormal(rng, mu, 0.65) / 100_000) * 100_000;
    // TLO gets a haircut.
    const adjustedSi = coverBasis === "tlo" ? Math.round(siUsd * 0.45) : siUsd;
    const premiumUsd = Math.round(adjustedSi * (0.0015 + rng() * 0.002));

    // DQ: higher failure rate for older, TLO, certain coverholders
    let failProb = 0.18;
    if (yearBuilt < 2000) failProb += 0.18;
    if (coverBasis === "tlo") failProb += 0.08;
    if (coverholder === "atlas") failProb += 0.1;
    if (coverholder === "coastal") failProb += 0.05;
    const dqStatus = rng() < failProb ? "fail" : "pass";

    const dqBuckets: Array<
      | "data type"
      | "mandatory"
      | "summation"
      | "conditional"
      | "duplicates"
      | "pattern"
      | "reference list"
      | "binary"
      | "other"
    > = [];
    let dqFailureCount = 0;
    if (dqStatus === "fail") {
      const choices = [
        ["reference list", 0.32] as const,
        ["data type", 0.2] as const,
        ["mandatory", 0.17] as const,
        ["duplicates", 0.09] as const,
        ["pattern", 0.06] as const,
        ["conditional", 0.07] as const,
        ["other", 0.04] as const,
        ["summation", 0.03] as const,
        ["binary", 0.02] as const,
      ];
      dqFailureCount = 1 + Math.floor(rng() * 3);
      for (let k = 0; k < dqFailureCount; k++) {
        const b = weighted(rng, choices);
        if (!dqBuckets.includes(b)) dqBuckets.push(b);
      }
    }

    const rowFlags: VesselFlag[] = [];
    if (yearBuilt < 1999) rowFlags.push({ label: "Age > 25 yrs", tone: "warn" });
    if (navZone !== "worldwide") rowFlags.push({ label: "War zone transit", tone: "warn" });
    if (dqBuckets.includes("duplicates")) rowFlags.push({ label: "Duplicate IMO", tone: "danger" });
    if (dqBuckets.includes("mandatory")) rowFlags.push({ label: "Missing SI", tone: "danger" });
    if (dqBuckets.includes("reference list"))
      rowFlags.push({ label: "Flag mismatch", tone: "warn" });
    if (dqBuckets.includes("data type"))
      rowFlags.push({ label: "Inception as string", tone: "warn" });

    const addedAt = new Date(mockToday - rng() * spreadMs).toISOString().slice(0, 10);

    vessels.push({
      imo: `9${(100000 + i * 137 + Math.floor(rng() * 500)).toString().slice(0, 6)}`,
      name: generateVesselName(rng, i),
      type: pick(rng, type.labels),
      typeKey: type.typeKey,
      coverBasis,
      flagIso2: flag.iso2,
      flagName: flag.name,
      flagIsoNum: flag.isoNum,
      yearBuilt,
      siUsd: adjustedSi,
      premiumUsd,
      classSociety,
      coverholder,
      dqStatus,
      dqFailureCount,
      dqBuckets,
      rowFlags,
      navZone,
      addedAt,
    });
  }

  return vessels;
}

const VESSEL_NAME_PREFIXES = ["MV", "MT", "SS", "MV", "MV"];
const VESSEL_NAME_WORDS_A = [
  "Adriatic",
  "Pacific",
  "Nordic",
  "Coral",
  "Southern",
  "Blue",
  "Poseidon",
  "Caspian",
  "Atlantic",
  "Polar",
  "Eastern",
  "Silver",
  "Golden",
  "Arctic",
  "Crimson",
  "Emerald",
  "Iron",
  "Celtic",
  "Baltic",
  "Tropic",
];
const VESSEL_NAME_WORDS_B = [
  "Star",
  "Venture",
  "Cross",
  "Frost",
  "Horizon",
  "Trader",
  "Eagle",
  "Wind",
  "Bay",
  "Dawn",
  "Voyager",
  "Spirit",
  "Crown",
  "Pearl",
  "Navigator",
  "Pioneer",
  "Endeavour",
  "Liberty",
  "Majesty",
  "Grace",
];

function generateVesselName(rng: () => number, i: number): string {
  const prefix = pick(rng, VESSEL_NAME_PREFIXES);
  const a = VESSEL_NAME_WORDS_A[i % VESSEL_NAME_WORDS_A.length] ?? "Pacific";
  const b = VESSEL_NAME_WORDS_B[(i * 3) % VESSEL_NAME_WORDS_B.length] ?? "Star";
  return `${prefix} ${a} ${b}`;
}

export const VESSELS: Vessel[] = buildVesselUniverse(247);

// ── Summary + header ────────────────────────────────────────

export const ANALYTICS_SUMMARY: AnalyticsSummary = {
  totalVessels: VESSELS.length,
  totalSumsInsuredUsd: VESSELS.reduce((a, v) => a + v.siUsd, 0),
  totalPremiumUsd: VESSELS.reduce((a, v) => a + v.premiumUsd, 0),
  dqPassRate: VESSELS.filter((v) => v.dqStatus === "pass").length / VESSELS.length,
  agedVesselCount: VESSELS.filter((v) => 2024 - v.yearBuilt > 20).length,
  agedVesselPercent: VESSELS.filter((v) => 2024 - v.yearBuilt > 20).length / VESSELS.length,
  newSinceLastImport: 12,
  lastImportedAt: "2026-03-13T08:42:00Z",
  coverholderName: "Hartwell Marine Underwriting Ltd",
  policyRef: "MH-2024-0041",
  inceptionDate: "2025-03-14",
  expiryDate: "2026-03-13",
};

// ── War-risk / nav-zone exposure ────────────────────────────

export function buildNavZoneExposure(vessels: Vessel[]): NavZoneExposureRow[] {
  const rows: Array<{
    zoneKey: NavZone;
    zoneLabel: string;
    description: string;
    severity: "danger" | "warn" | "neutral";
  }> = [
    {
      zoneKey: "red_sea",
      zoneLabel: "Red Sea / Gulf of Aden",
      description: "Houthi attack corridor · JWC listed",
      severity: "danger",
    },
    {
      zoneKey: "hormuz",
      zoneLabel: "Strait of Hormuz",
      description: "Iranian detention risk · IRGC activity",
      severity: "danger",
    },
    {
      zoneKey: "black_sea",
      zoneLabel: "Black Sea",
      description: "Ukraine conflict zone · restricted",
      severity: "warn",
    },
    {
      zoneKey: "gulf_of_guinea",
      zoneLabel: "Gulf of Guinea",
      description: "Piracy risk zone · IMB monitored",
      severity: "warn",
    },
    {
      zoneKey: "worldwide",
      zoneLabel: "Worldwide trading",
      description: "Unlimited navigational limits",
      severity: "neutral",
    },
  ];

  return rows.map((r) => {
    const match = vessels.filter((v) => v.navZone === r.zoneKey);
    const siUsd = match.reduce((a, v) => a + v.siUsd, 0);
    return {
      ...r,
      vesselCount: match.length,
      siUsd,
    };
  });
}

// ── Deductible structure (static list; counts filterable) ──

export function buildDeductibleStructure(vessels: Vessel[]): DeductibleRow[] {
  const hmFullCount = vessels.filter((v) => v.coverBasis === "hm_full").length;
  const tloCount = vessels.filter((v) => v.coverBasis === "tlo").length;
  return [
    { peril: "All Risks (H&M full)", vesselCount: hmFullCount, range: "$25k – $500k" },
    { peril: "Total Loss Only", vesselCount: tloCount, range: "Nil deductible" },
    {
      peril: "War & Strikes",
      vesselCount: Math.round(vessels.length * 0.35),
      range: "$50k – $250k",
    },
    {
      peril: "Machinery Breakdown",
      vesselCount: Math.round(vessels.length * 0.57),
      range: "$10k – $100k",
    },
    {
      peril: "Pollution / Oil Spillage",
      vesselCount: Math.round(vessels.length * 0.25),
      range: "$100k – $1M",
    },
    { peril: "General Average", vesselCount: hmFullCount, range: "Per policy terms" },
    {
      peril: "No deductible declared",
      vesselCount: Math.round(vessels.length * 0.07),
      range: "Needs review",
      needsReview: true,
    },
  ];
}

// ── Field completeness ──────────────────────────────────────

export const FIELD_COMPLETENESS: FieldCompletenessDatum[] = [
  { fieldName: "Vessel Name", populationRate: 1.0, mandatory: true },
  { fieldName: "IMO", populationRate: 0.99, mandatory: true },
  { fieldName: "Sums Insured", populationRate: 0.98, mandatory: true },
  { fieldName: "Inception Date", populationRate: 0.97, mandatory: true },
  { fieldName: "Vessel Flag", populationRate: 0.95, mandatory: true },
  { fieldName: "Vessel Class", populationRate: 0.93, mandatory: true },
  { fieldName: "Vessel Type", populationRate: 0.91, mandatory: true },
  { fieldName: "Deductible", populationRate: 0.93, mandatory: true },
  { fieldName: "Vessel DWT", populationRate: 0.87, mandatory: false },
  { fieldName: "Year Built", populationRate: 0.86, mandatory: false },
  { fieldName: "Vessel Owner", populationRate: 0.84, mandatory: false },
  { fieldName: "Vessel Manager", populationRate: 0.79, mandatory: false },
  { fieldName: "MMSI", populationRate: 0.74, mandatory: false },
  { fieldName: "Nav Limits", populationRate: 0.68, mandatory: false },
  { fieldName: "Purchase Price", populationRate: 0.61, mandatory: false },
  { fieldName: "Crew Description", populationRate: 0.52, mandatory: false },
];

// ── Automation rate over time (streamgraph) ────────────────

function buildAutomationRateSeries(): AutomationRatePoint[] {
  const rng = makeRng(0x0a721f);
  const points: AutomationRatePoint[] = [];
  const weeks = 78; // 18 months
  const end = new Date("2026-03-13T00:00:00Z").getTime();
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;

  for (let w = weeks - 1; w >= 0; w--) {
    const t = w / (weeks - 1); // 0 (oldest) → 1 (newest)
    // Tier share evolution: Green grows, Amber/Red/Unknown shrink, Manual decays.
    const green = 0.42 + 0.32 * (1 - t) + (rng() - 0.5) * 0.03;
    const amber = 0.28 - 0.12 * (1 - t) + (rng() - 0.5) * 0.02;
    const red = 0.15 - 0.05 * (1 - t) + (rng() - 0.5) * 0.015;
    const unknown = 0.08 - 0.05 * (1 - t) + (rng() - 0.5) * 0.01;
    const manual = 0.07 - 0.05 * (1 - t) + (rng() - 0.5) * 0.01;
    const total = green + amber + red + unknown + manual;

    // Normalise to ~100% and scale by weekly throughput
    const weeklyColumns = Math.round(380 + 160 * (1 - t) + rng() * 80);
    const date = new Date(end - w * msPerWeek).toISOString().slice(0, 10);
    points.push({
      date,
      green: Math.round((green / total) * weeklyColumns),
      amber: Math.round((amber / total) * weeklyColumns),
      red: Math.round((red / total) * weeklyColumns),
      unknown: Math.round((unknown / total) * weeklyColumns),
      manual: Math.round((manual / total) * weeklyColumns),
    });
  }
  return points;
}

export const AUTOMATION_RATE_SERIES: AutomationRatePoint[] = buildAutomationRateSeries();

// ── KB growth (cumulative) ──────────────────────────────────

function buildKbGrowth(): KbGrowthPoint[] {
  const rng = makeRng(0x4cbb17);
  const weeks = 78;
  const end = new Date("2026-03-13T00:00:00Z").getTime();
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  const start = 42;
  const points: KbGrowthPoint[] = [];
  let total = start;
  for (let w = weeks - 1; w >= 0; w--) {
    const date = new Date(end - w * msPerWeek).toISOString().slice(0, 10);
    const weeklyNew = Math.max(0, Math.round(4 + rng() * 10 - w * 0.02));
    total += weeklyNew;
    points.push({ date, totalMappings: total });
  }
  // Reverse to chronological order (oldest first is how the reducer builds).
  // Build above emits oldest-first already (w descends; push in loop), so this is correct.
  return points;
}

export const KB_GROWTH_SERIES: KbGrowthPoint[] = buildKbGrowth();

// ── Work-done counters with sparklines ──────────────────────

function sparkline(rng: () => number, baseline: number, growth: number, n = 14): number[] {
  const out: number[] = [];
  let v = baseline;
  for (let i = 0; i < n; i++) {
    v += Math.max(0, growth + (rng() - 0.3) * growth * 0.6);
    out.push(Math.round(v));
  }
  return out;
}

export const WORK_DONE_COUNTERS: WorkDoneCounter[] = [
  {
    id: "columns-auto-mapped",
    label: "Columns auto-mapped",
    value: 24817,
    unit: "columns",
    sparkline: sparkline(makeRng(1), 22000, 210),
  },
  {
    id: "cells-validated",
    label: "Cells validated",
    value: 1247592,
    unit: "cells",
    sparkline: sparkline(makeRng(2), 1100000, 12000),
  },
  {
    id: "files-processed",
    label: "Files processed",
    value: 847,
    unit: "files",
    sparkline: sparkline(makeRng(3), 720, 10),
  },
  {
    id: "mappings-learned",
    label: "Mappings learned",
    value: 312,
    unit: "known",
    sparkline: sparkline(makeRng(4), 240, 6),
  },
];

// ── Human attention budget ──────────────────────────────────

export const HUMAN_ATTENTION: HumanAttentionSegment[] = [
  { level: "fully_automated", label: "Fully automated", fileCount: 491, percent: 0.58 },
  { level: "light_touch", label: "Light-touch review", fileCount: 229, percent: 0.27 },
  { level: "heavy_touch", label: "Heavy review", fileCount: 93, percent: 0.11 },
  { level: "blocked", label: "Blocked, reviewer required", fileCount: 34, percent: 0.04 },
];

// ── Before/after file drill-in ──────────────────────────────

const BEFORE_AFTER_SOURCE_FIELDS = [
  "vessel_name",
  "IMO_No",
  "Ship Type",
  "Flag State",
  "YEAR_BUILT",
  "Sum_Insured_USD",
  "Deductible",
  "Class_Society",
  "MMSI",
  "Policy_No",
  "UW_Year",
  "Inception Dt",
  "Expiry Dt",
  "Owner_Entity",
  "Manager_Entity",
  "P&I Club",
  "DWT_tonnes",
  "GT",
  "H&M Rate",
  "Nav_Zone",
  "Co-Insurer",
  "Premium_USD",
  "Crew_Descr",
  "Purchase_Price",
  "Hull_Material",
  "Reg_Port",
  "Keel_Laid_Dt",
  "Delivery_Dt",
  "Prior_Claims",
  "Loss_Record",
  "Trade_Pattern",
  "Protection_Class",
];

function buildBeforeAfter(
  fileId: string,
  fileName: string,
  coverholder: string,
  seed: number
): FileBeforeAfter {
  const rng = makeRng(seed);
  const total = 28 + Math.floor(rng() * 16);
  const columns: FileBeforeAfter["columns"] = [];
  let autoMapped = 0;
  let lightReview = 0;
  let manualMapped = 0;
  let unresolved = 0;

  for (let i = 0; i < total; i++) {
    const src = BEFORE_AFTER_SOURCE_FIELDS[i % BEFORE_AFTER_SOURCE_FIELDS.length] ?? "column";
    const roll = rng();
    if (roll < 0.68) {
      autoMapped++;
      columns.push({
        sourceColumn: src,
        targetField: src.replace(/[_\s]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        tier: "Green",
        confidence: 0.95 + rng() * 0.05,
      });
    } else if (roll < 0.82) {
      lightReview++;
      columns.push({
        sourceColumn: src,
        targetField: src.replace(/[_\s]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        tier: "Amber",
        confidence: 0.72 + rng() * 0.15,
      });
    } else if (roll < 0.93) {
      manualMapped++;
      columns.push({
        sourceColumn: src,
        targetField: src.replace(/[_\s]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        tier: "Red",
        confidence: 0.5 + rng() * 0.15,
      });
    } else {
      unresolved++;
      columns.push({
        sourceColumn: src,
        targetField: null,
        tier: "Unknown",
        confidence: 0,
      });
    }
  }

  return {
    fileId,
    fileName,
    coverholder,
    totalColumns: total,
    autoMapped,
    lightReview,
    manualMapped,
    unresolved,
    columns,
  };
}

export const BEFORE_AFTER_FILES: FileBeforeAfter[] = [
  buildBeforeAfter("file-0041", "hartwell_marine_schedule_2024Q1.xlsx", "hartwell", 101),
  buildBeforeAfter("file-0042", "nordic_uw_vessels_mar24.xml", "nordic", 202),
  buildBeforeAfter("file-0043", "atlas_marine_fleet_april.xlsx", "atlas", 303),
  buildBeforeAfter("file-0044", "coastal_re_bordereau_q1.xlsx", "coastal", 404),
  buildBeforeAfter("file-0045", "hartwell_marine_addendum_04.xlsx", "hartwell", 505),
  buildBeforeAfter("file-0046", "nordic_uw_vessels_apr24.json", "nordic", 606),
];

// ── Date helpers for filter slicing ─────────────────────────

export function isWithinDateRange(iso: IsoDate, from: IsoDate, to: IsoDate): boolean {
  return iso >= from && iso <= to;
}
